import { useState } from "react";
import { MessageSquareText, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { trackLead } from "@/lib/track";
import { getUtm } from "@/lib/utm";

export default function TextUsTab() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", message: "", company: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/text-us", { ...form, ...getUtm() });
      trackLead("text_us");
      toast.success("Got it! We'll text you right back.");
      setForm({ name: "", phone: "", message: "", company: "" });
      setOpen(false);
    } catch (err) {
      toast.error(apiError(err, "Couldn't send. Please call (972) 742-3944."));
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full rounded-sm border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-red-600";

  return (
    <>
      {/* Side tab */}
      <button
        onClick={() => setOpen(true)}
        className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-red-600 hover:bg-red-700 text-white font-bold text-sm px-2.5 py-4 rounded-l-md shadow-lg shadow-black/30 transition-colors flex flex-col items-center gap-2 [writing-mode:vertical-rl] rotate-180"
        data-testid="text-us-tab"
        aria-label="Text us"
      >
        <MessageSquareText className="h-4 w-4 rotate-90" /> Text Us
      </button>

      {/* Panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-6" data-testid="text-us-panel">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-full sm:w-[380px] bg-white rounded-t-lg sm:rounded-sm shadow-2xl animate-fade-up">
            <div className="bg-slate-900 text-white px-5 py-4 flex items-center justify-between rounded-t-lg sm:rounded-t-sm">
              <div className="flex items-center gap-2">
                <MessageSquareText className="h-5 w-5 text-red-500" />
                <div>
                  <p className="font-heading font-bold leading-tight">Text Us</p>
                  <p className="text-xs text-slate-400">We reply fast — usually within minutes.</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="text-us-close">
                <X className="h-5 w-5 text-slate-300 hover:text-white" />
              </button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-3" data-testid="text-us-form">
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                <label>
                  Company
                  <input type="text" name="company" tabIndex={-1} autoComplete="off" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                </label>
              </div>
              <input className={input} placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="text-us-name" />
              <input className={input} type="tel" placeholder="Mobile number *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="text-us-phone" />
              <textarea className={input} rows={3} placeholder="How can we help? (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="text-us-message" />
              <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-70 text-white font-bold px-5 py-3 rounded-sm transition-colors" data-testid="text-us-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send Text</>}
              </button>
              <p className="text-[11px] text-slate-400 text-center">By texting us you agree to receive SMS replies about your request. Msg &amp; data rates may apply.</p>
            </form>
            <div className="px-5 pb-5 -mt-2">
              <a
                href="sms:+19727423944?&body=Hi,%20I'm%20interested%20in%20garage%20door%20service"
                className="block text-center text-sm font-bold text-red-600 hover:text-red-700"
                data-testid="text-us-direct"
              >
                Or text us directly from your phone →
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
